import { useState } from 'react';
import styled from 'styled-components';
import {
  ButtonIcon,
  cssColorValue,
  cssSpacing,
  gray90,
  IconFlag,
  IconHelpCircle,
  SemanticBackgroundColor,
  TooltipTrigger,
  Type,
  VisuallyHidden,
} from '@spotify-internal/encore-web';
import {
  FlexCenterVertical,
  TooltipWithPadding,
} from '@components/shared/styles';

export const Container = styled.div<{
  marginBottom?: boolean;
  backgroundColor?: SemanticBackgroundColor;
}>`
  display: flex;
  align-items: center;
  height: ${cssSpacing('looser-3', '48px')};
  padding-top: 14px;
  padding-bottom: 14px;
  border-bottom: solid 1px ${gray90};

  margin-bottom: ${({ marginBottom }) => (marginBottom ? '24px' : '2px')};
  ${({ backgroundColor }) =>
    backgroundColor &&
    `
    background-color: ${cssColorValue(backgroundColor)};
  `};
`;

const Label = styled(Type)`
  min-inline-size: 200px;
  padding-left: ${cssSpacing('tighter-3', '12px')};
  width: 200px;

  display: flex;
  align-items: center;

  svg {
    margin-left: ${cssSpacing('tighter-4', '8px')};
  }
`;

const Value = styled.div`
  flex: 1;
  padding-left: ${cssSpacing('tighter-3', '12px')};
`;

const ButtonIconMarginRight = styled(ButtonIcon)`
  margin-right: 10px;
`;

interface PillInfoProps {
  label: string;
  labelTooltipMessage?: string;
  value: string | React.ReactNode;
  tooltipMessage: string | undefined;
  backgroundColor?: SemanticBackgroundColor;
  hasMarginBottom?: boolean;
  hasTextBrightAccent?: boolean;
  onValueIconHover?: () => void;
  onLabelIconHover?: () => void;
}

export const PillInfo = ({
  label,
  labelTooltipMessage,
  value,
  hasMarginBottom = false,
  hasTextBrightAccent = false,
  tooltipMessage = undefined,
  backgroundColor,
  onValueIconHover,
  onLabelIconHover,
}: PillInfoProps) => {
  const [showLabelTooltip, toggleShowLabelTooltip] = useState(false);
  const [showValueTooltip, toggleShowValueTooltip] = useState(false);

  const handleValueIconHover = () => {
    if (onValueIconHover) {
      onValueIconHover();
    }

    toggleShowValueTooltip(true);
  };

  const handleLabelIconHover = () => {
    if (onLabelIconHover) {
      onLabelIconHover();
    }

    toggleShowLabelTooltip(true);
  };

  return (
    <Container marginBottom={hasMarginBottom} backgroundColor={backgroundColor}>
      <Label variant="viola" semanticColor="textSubdued">
        {label}
        {labelTooltipMessage && (
          <TooltipTrigger
            overlay={
              showLabelTooltip && (
                <TooltipWithPadding centeredText>
                  <Type
                    variant="viola"
                    dangerouslySetInnerHTML={{
                      __html: labelTooltipMessage,
                    }}
                  />
                </TooltipWithPadding>
              )
            }
            placement={TooltipTrigger.top}
            onMouseEnter={handleLabelIconHover}
            onMouseLeave={() => toggleShowLabelTooltip(false)}
          >
            <IconHelpCircle
              iconSize={16}
              aria-label={`More information for ${label}`}
              semanticColor="textSubdued"
            />
            <VisuallyHidden>More information help icon</VisuallyHidden>
          </TooltipTrigger>
        )}
      </Label>
      <Value>
        <FlexCenterVertical>
          {tooltipMessage && (
            <TooltipTrigger
              overlay={
                showValueTooltip && (
                  <TooltipWithPadding centeredText>
                    <Type
                      variant="viola"
                      dangerouslySetInnerHTML={{
                        __html: tooltipMessage,
                      }}
                    />
                  </TooltipWithPadding>
                )
              }
              placement={TooltipTrigger.top}
              onMouseEnter={handleValueIconHover}
              onMouseLeave={() => toggleShowValueTooltip(false)}
            >
              <ButtonIconMarginRight
                variant="tertiary"
                aria-label={`Flagged ${label.toLocaleLowerCase()}`}
              >
                <IconFlag
                  iconSize={16}
                  semanticColor="textBase"
                />
              </ButtonIconMarginRight>
            </TooltipTrigger>
          )}
          {typeof value === 'string' ? (
            <Type
              variant="viola"
              semanticColor={
                hasTextBrightAccent ? 'textBrightAccent' : 'textBase'
              }
            >
              {value}
            </Type>
          ) : (
            value
          )}
        </FlexCenterVertical>
      </Value>
    </Container>
  );
};
